/*
 * Set-password page, reached from a one-time invite link.
 *
 * Standalone by design - it reads the token straight from the URL and does not
 * assume a signed-in user or the app router, so it can render before the auth
 * gate. On success the server sets the session cookie and we hard-navigate to
 * the app, which is the simplest way to land signed in.
 */

import { useEffect, useState } from 'react'
import { Boxes } from 'lucide-react'
import { ApiError, api } from '../lib/api'

export function SetPassword() {
  const token = new URLSearchParams(window.location.search).get('token') || ''
  const [invite, setInvite] = useState<{ email: string; name: string } | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) { setLoadErr('This link is missing its token.'); return }
    api.get<{ email: string; name: string }>(`/api/auth/invite/${token}`)
      .then(setInvite)
      .catch((e) => setLoadErr(e instanceof ApiError ? e.message
        : 'This link is invalid or has expired.'))
  }, [token])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (pw !== confirm) { setError('The passwords do not match.'); return }
    setBusy(true)
    try {
      await api.post('/api/auth/set-password', { token, password: pw })
      window.location.href = '/'
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set the password.')
      setBusy(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20 }}>
      <div className="card" style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <div className="row" style={{ justifyContent: 'center', gap: 10, marginBottom: 6 }}>
          <Boxes size={26} color="#3aa0ff" />
          <div style={{ fontWeight: 750, fontSize: 20, letterSpacing: '-.02em' }}>
            GRAYSON <span style={{ color: 'var(--blue-2)' }}>KART</span>
          </div>
        </div>

        {loadErr ? (
          <div className="advice warn" style={{ textAlign: 'left', marginTop: 16 }}>
            {loadErr} Ask for a new invite link.
          </div>
        ) : !invite ? (
          <p className="muted small">Checking your link…</p>
        ) : (
          <>
            <p className="muted small" style={{ marginTop: 0, marginBottom: 20 }}>
              Welcome{invite.name ? `, ${invite.name}` : ''}. Set a password for{' '}
              <strong>{invite.email}</strong>.
            </p>
            <form onSubmit={submit} style={{ textAlign: 'left' }}>
              <label className="tiny dim" htmlFor="sp-pw">New password</label>
              <input id="sp-pw" type="password" autoComplete="new-password"
                value={pw} onChange={(e) => setPw(e.target.value)} required
                style={{ width: '100%', marginBottom: 10 }} />
              <label className="tiny dim" htmlFor="sp-cf">Confirm password</label>
              <input id="sp-cf" type="password" autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                style={{ width: '100%', marginBottom: 14 }} />
              <button className="btn" type="submit" disabled={busy}
                style={{ width: '100%', justifyContent: 'center' }}>
                {busy ? 'Setting…' : 'Set password and sign in'}
              </button>
            </form>
            <p className="tiny dim" style={{ marginTop: 14, marginBottom: 0 }}>
              At least 8 characters.
            </p>
          </>
        )}

        {error && (
          <div className="advice warn" style={{ textAlign: 'left', marginTop: 16 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}
